import React, {useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {clearSelection} from 'src/components/data-table-selection';
import {USER_STATES} from 'src/constants';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {selectAccessRights} from 'src/hooks/access-rights/services/access-rights-slice';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Item} from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {useGateValue} from '@pb/kit-lib-statsig';


import {
    SELECTION_KEY,
    UsersGroupsSearchResultController,
    UsersGroupsSearchResultView,
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from '../users-groups-search-result';

import {Header} from './components/header';
import {UsersGroupsToolsRibbon} from './components/users-groups-tools-ribbon';
import {getStatusesFromState, getTypesFromState} from './utils';

import type {UserSearchStateCriteria} from 'src/constants';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';

export const UsersTabWrapper = () => {
    const provisioningEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const rights = useSelector(selectAccessRights);
    const productsAllowed = provisioningEnabled && !!rights?.includes('USER_PRODUCTS_VIEW');
    const queryParams = useParams<{accountId: string, userType?: string}>();
    const accountId = queryParams.accountId;
    const stateType = !provisioningEnabled && queryParams.userType
        ? USER_STATES[queryParams.userType]
        : 'all';

    const dispatch = useDispatch();
    const cacheKeys = useSelector(userSearchResultStateSelector);
    const searchCriteria = cacheKeys.searchCriteria as UserSearchCriteria;

    const context = useMemo(() => ({accountId, stateType, provisioningEnabled}),
        [accountId, stateType, provisioningEnabled]);
    const previousContext = useRef<typeof context | null>(null);
    const contextChanged = !isDeepEqual({prev: previousContext.current, current: context});

    useEffect(() => {
        const previous = previousContext.current;
        previousContext.current = context;
        if (!context.accountId) {
            return;
        }
        if (contextChanged) {
            const {platformAccess, productExternalKeys, ...other} = searchCriteria;
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(userSearchResultStateActions.setSearchCriteria({
                ...previous?.accountId === context.accountId ? other : {},
                userTypes: getTypesFromState(context.stateType as UserSearchStateCriteria | 'all'),
                userStatuses: getStatusesFromState(context.stateType as UserSearchStateCriteria | 'all'),
                accountIds: [+context.accountId],
            }));
        } else if (!productsAllowed && searchCriteria.productExternalKeys?.length) {
            const {productExternalKeys, ...other} = searchCriteria;
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(userSearchResultStateActions.setSearchCriteria(other));
        }
    }, [context, contextChanged, productsAllowed, searchCriteria, dispatch]);

    if (!accountId) {
        return null;
    }

    const ready = !contextChanged && !!searchCriteria.accountIds?.includes(+accountId) &&
        (productsAllowed || !searchCriteria.productExternalKeys?.length);
    return <UsersTab accountId={+accountId} ready={ready}/>;
};

const PLACE = 'Users & Groups';

type Props = {
    accountId: number,
    ready: boolean,
};

const UsersTab = ({accountId, ready}: Props) => (
    <UsersGroupsSearchResultController place={PLACE}>
        {props => (
            <Item grow={1}>
                {ready ? (
                    <>
                        <Header refreshVersion={props.refreshVersion}/>
                        <StickyElement className="sticky-tool-ribbon">
                            <UsersGroupsToolsRibbon
                                accountId={accountId}
                                place={PLACE}
                                adminId={props.adminId}
                                isLoading={props.isLoading}
                                getAllEntityIds={props.getAllEntityIds}
                                templates={props.templates}
                                activeSearchTemplate={props.activeSearchTemplate}
                                setActiveSearchTemplate={props.setActiveSearchTemplate}
                                loadTemplates={props.loadTemplates}
                                refreshResults={props.refreshResults}
                                loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                            />
                        </StickyElement>
                        <UsersGroupsSearchResultView
                            enableUserDetails={true}
                            onAccessChanged={props.refreshAccessResults}
                            getAllEntityIds={props.getAllEntityIds}
                            place={PLACE}
                            activeSearchTemplate={props.activeSearchTemplate}
                        />
                    </>
                ) : null}
            </Item>
        )}
    </UsersGroupsSearchResultController>
);
